/**
 * Only one card is selected at a time, and selecting is what reveals its reply
 * composer — so the panel stays a list of quiet cards until you act on one.
 */
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import {
  ArrowDownIcon,
  ArrowUpIcon,
  EyeIcon,
  EyeOffIcon,
  MessageSquareDashedIcon,
  XIcon,
} from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  AlertAction,
  AlertDescription,
  AlertTitle,
  Button,
  TooltipProvider,
} from '@monksflow/monks-ui';

import {
  trackerCommentsListQueryOptions,
  useCreateTrackerCommentMutation,
  type TrackerCommentResponseDto,
} from '@/entities/tracker-comment';
import { CommentInput } from '@/entities/project-comment';
import { notificationService } from '@/lib/services/notification.service';
import { EmojiPicker, EmptyState } from '@/shared/ui';
import { track } from '@/shared/analytics';

import { buildCommentThreads } from '../../lib/commentThreads';
import { useCanEditTracker } from '../../lib/tracker-access';
import type { TrackerBoardType } from '../../lib/trackers-analytics';
import { CommentsSkeletonList } from './CommentsSkeletonList';
import { RowAutomationActivity } from './RowAutomationActivity';
import { TrackerCommentCard } from './TrackerCommentCard';

interface ThreadedCommentsBodyProps {
  trackerId: string;
  boardType: TrackerBoardType;
  rowKey: string;
  focusCommentId: string | null;
  selectedCommentId: string | null;
  onSelectComment: (commentId: string | null) => void;
  onClose: () => void;
}

export function ThreadedCommentsBody({
  trackerId,
  boardType,
  rowKey,
  focusCommentId,
  selectedCommentId,
  onSelectComment,
  onClose,
}: ThreadedCommentsBodyProps) {
  const { t } = useTranslation('trackers');
  const canEdit = useCanEditTracker();

  const [showResolved, setShowResolved] = useState(false);
  const [newestFirst, setNewestFirst] = useState(false);
  const [expandedThreads, setExpandedThreads] = useState<ReadonlySet<string>>(new Set());

  const expandThread = useCallback((id: string) => {
    setExpandedThreads((open) => (open.has(id) ? open : new Set(open).add(id)));
  }, []);

  const toggleThread = useCallback((id: string) => {
    setExpandedThreads((open) => {
      const next = new Set(open);
      if (!next.delete(id)) next.add(id);
      return next;
    });
  }, []);

  const commentsQuery = useQuery(trackerCommentsListQueryOptions({ trackerId, rowKey }));
  const createMutation = useCreateTrackerCommentMutation({ trackerId, rowKey });

  const items = commentsQuery.data?.items ?? [];
  // Only when nothing loaded: a 404 on a refetch keeps the thread already read on screen.
  const threadUnavailable =
    items.length === 0 &&
    axios.isAxiosError(commentsQuery.error) &&
    commentsQuery.error.response?.status === 404;

  const target = items.find((c) => c.id === focusCommentId);
  const targetRoot = target?.parentId ? items.find((c) => c.id === target.parentId) : target;

  // A deeplink has to land on its comment even when that thread is settled, or
  // the reader following the link is shown an empty panel. `rowKey` is in the
  // deps so switching rows drops the previous row's override.
  const linkedToResolved = targetRoot?.resolved ?? false;
  useEffect(() => {
    setShowResolved(linkedToResolved);
  }, [rowKey, linkedToResolved]);

  // Same for a deeplinked reply: its thread opens so the card can scroll itself
  // into view. Seeding the state rather than forcing it leaves it collapsible.
  const focusedReplyRootId = target?.parentId ?? null;
  useEffect(() => {
    if (focusedReplyRootId !== null) expandThread(focusedReplyRootId);
  }, [focusedReplyRootId, expandThread]);

  const threads = buildCommentThreads(items, { showResolved, newestFirst });
  const allHidden = items.length > 0 && threads.length === 0;

  // Rethrown after the notice: the composer keeps what was typed only while the
  // promise it was given rejects, and swallowing the error here would clear the
  // box on a comment that was never posted.
  const addComment = async (content: Record<string, unknown>, parentId?: string) => {
    try {
      await createMutation.mutateAsync({ content, parentId });
    } catch (error) {
      notificationService.showError(t('trackersBoard.trackerCommentsPanel.couldNotPostComment'), {
        description: t('trackersBoard.trackerCommentsPanel.couldNotPostCommentDescription'),
      });
      throw error;
    }
    track('tracker_comment_added', { tracker_id: trackerId, board_type: boardType });
  };

  return (
    <div className="flex h-full flex-col">
      <header className="flex items-center justify-between gap-3 border-b border-border px-4 py-3">
        <span className="text-sm font-semibold text-foreground">
          {t('trackersBoard.trackerCommentsPanel.comments')}
        </span>
        <Button
          type="button"
          variant="ghost"
          size="icon-mini"
          aria-label={t('trackersBoard.trackerCommentsPanel.closeCommentsPanel')}
          onClick={onClose}
          className="shrink-0"
        >
          <XIcon aria-hidden />
        </Button>
      </header>

      <RowAutomationActivity trackerId={trackerId} rowKey={rowKey} />

      <div className="flex items-center justify-between gap-2 border-b border-border px-3 py-2">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          aria-pressed={showResolved}
          title={
            showResolved
              ? t('trackersBoard.trackerCommentsPanel.hideResolvedComments')
              : t('trackersBoard.trackerCommentsPanel.showResolvedComments')
          }
          onClick={() => setShowResolved((on) => !on)}
        >
          {showResolved ? (
            <EyeIcon className="size-4" aria-hidden />
          ) : (
            <EyeOffIcon className="size-4 text-muted-foreground" aria-hidden />
          )}
          {t('trackersBoard.trackerCommentsPanel.resolvedComments')}
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          aria-pressed={newestFirst}
          title={
            newestFirst
              ? t('trackersBoard.trackerCommentsPanel.showOldestFirst')
              : t('trackersBoard.trackerCommentsPanel.showNewestFirst')
          }
          onClick={() => setNewestFirst((on) => !on)}
        >
          {newestFirst ? (
            <ArrowDownIcon className="size-4" aria-hidden />
          ) : (
            <ArrowUpIcon className="size-4" aria-hidden />
          )}
          {newestFirst
            ? t('trackersBoard.trackerCommentsPanel.newest')
            : t('trackersBoard.trackerCommentsPanel.oldest')}
        </Button>
      </div>

      {/* Clicking past the cards drops the selection, closing an open reply composer. */}
      <div
        role="presentation"
        className="flex-1 overflow-y-auto px-3 py-3"
        onClick={() => onSelectComment(null)}
      >
        {commentsQuery.isLoading ? (
          <CommentsSkeletonList />
        ) : threadUnavailable ? (
          <EmptyState
            icon={<MessageSquareDashedIcon aria-hidden />}
            title={t('trackersBoard.trackerCommentsPanel.threadUnavailable')}
          />
        ) : commentsQuery.isError && items.length === 0 ? (
          // Ahead of the empty state, which would otherwise invite someone to
          // start a thread that already exists and that they cannot see. Keyed
          // on what loaded rather than on what is on screen: a row whose every
          // comment is resolved has none showing and still loaded fine, and
          // reporting that as a failure hides the toggle that would reveal it.
          <EmptyState
            role="alert"
            icon={<MessageSquareDashedIcon aria-hidden />}
            title={t('trackersBoard.trackerCommentsPanel.couldNotLoadComments')}
            description={t('trackersBoard.trackerCommentsPanel.couldNotLoadCommentsDescription')}
          >
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => void commentsQuery.refetch()}
            >
              {t('trackersBoard.trackerCommentsPanel.tryAgain')}
            </Button>
          </EmptyState>
        ) : (
          <>
            {/* A refetch that failed over comments already read. They stay:
                taking them away is a worse answer than showing them slightly
                stale, and the banner is what says which they are. */}
            {commentsQuery.isError && (
              <Alert variant="destructive" role="alert" className="mb-2">
                <AlertTitle>
                  {t('trackersBoard.trackerCommentsPanel.couldNotRefreshComments')}
                </AlertTitle>
                <AlertDescription>
                  {t('trackersBoard.trackerCommentsPanel.showingWhatLoadedLast')}
                </AlertDescription>
                <AlertAction>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => void commentsQuery.refetch()}
                  >
                    {t('trackersBoard.trackerCommentsPanel.tryAgain')}
                  </Button>
                </AlertAction>
              </Alert>
            )}
            {threads.length === 0 ? (
              <EmptyState
                icon={
                  allHidden ? <EyeOffIcon aria-hidden /> : <MessageSquareDashedIcon aria-hidden />
                }
                title={
                  allHidden
                    ? t('trackersBoard.trackerCommentsPanel.everyCommentIsResolved')
                    : t('trackersBoard.trackerCommentsPanel.noCommentsYet')
                }
                description={
                  allHidden
                    ? t('trackersBoard.trackerCommentsPanel.showResolvedCommentsTo')
                    : t('trackersBoard.trackerCommentsPanel.startTheThreadItStays')
                }
              />
            ) : (
              <ul className="flex flex-col gap-2">
                {threads.map(({ comment, replies }) => (
                  <li key={comment.id}>
                    <ThreadCard
                      comment={comment}
                      replies={replies}
                      trackerId={trackerId}
                      rowKey={rowKey}
                      focusCommentId={focusCommentId}
                      selected={selectedCommentId === comment.id}
                      expanded={expandedThreads.has(comment.id)}
                      onSelect={() => onSelectComment(comment.id)}
                      onToggleReplies={() => toggleThread(comment.id)}
                      onReply={async (content) => {
                        await addComment(content, comment.id);
                        expandThread(comment.id);
                      }}
                    />
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
      </div>

      <footer className="border-t border-border px-3 py-3">
        {threadUnavailable ? null : canEdit ? (
          <CommentInput
            placeholder={t('trackersBoard.trackerCommentsPanel.writeAComment')}
            onSave={(content) => addComment(content)}
            leftActions={({ insertText }) => (
              <TooltipProvider delayDuration={300}>
                <EmojiPicker onSelect={insertText} />
              </TooltipProvider>
            )}
          />
        ) : (
          <p className="px-1 text-sm text-muted-foreground">
            {t('trackersBoard.viewOnly.commentsNotice')}
          </p>
        )}
      </footer>
    </div>
  );
}

interface ThreadCardProps {
  comment: TrackerCommentResponseDto;
  replies: TrackerCommentResponseDto[];
  trackerId: string;
  rowKey: string;
  focusCommentId: string | null;
  selected: boolean;
  expanded: boolean;
  onSelect: () => void;
  onToggleReplies: () => void;
  onReply: (content: Record<string, unknown>) => Promise<void>;
}

function ThreadCard({
  comment,
  replies,
  trackerId,
  rowKey,
  focusCommentId,
  selected,
  expanded,
  onSelect,
  onToggleReplies,
  onReply,
}: ThreadCardProps) {
  const { t } = useTranslation('trackers');

  return (
    <div
      role="presentation"
      onClick={(e) => {
        e.stopPropagation();
        onSelect();
      }}
      onFocusCapture={onSelect}
      className={
        'rounded-lg border p-3 transition-colors ' +
        (selected ? 'border-border bg-card shadow-sm' : 'border-transparent hover:bg-accent/40')
      }
    >
      <TrackerCommentCard
        comment={comment}
        trackerId={trackerId}
        rowKey={rowKey}
        isFocused={comment.id === focusCommentId}
        selected={selected}
        resolvable
        onReply={onReply}
        footerSlot={
          replies.length > 0 ? (
            <button
              type="button"
              className="self-start text-xs font-semibold text-primary"
              aria-expanded={expanded}
              onClick={(e) => {
                e.stopPropagation();
                onToggleReplies();
              }}
            >
              {t('trackersBoard.trackerCommentsPanel.replyCount', { count: replies.length })}
            </button>
          ) : null
        }
      >
        {expanded ? (
          <ul className="ml-11 flex flex-col gap-3">
            {replies.map((reply) => (
              <li key={reply.id}>
                <TrackerCommentCard
                  comment={reply}
                  trackerId={trackerId}
                  rowKey={rowKey}
                  isFocused={reply.id === focusCommentId}
                />
              </li>
            ))}
          </ul>
        ) : null}
      </TrackerCommentCard>
    </div>
  );
}
